"use client";
import type { ReactNode } from "react";
import type { SurveyQuestion } from "@/types/survey";
import { motion } from "motion/react";
import choiceButtonStyles from "./ChoiceButtonLayout.module.css";
import { getRatingConfig, type RatingScale } from "./ratingDefaults";

interface RatingQuestionProps {
  question: SurveyQuestion;
  brandColor?: string;
  isSelected?: (
    question: SurveyQuestion,
    candidate: string | number,
  ) => boolean;
  onAnswerChange?: (question: SurveyQuestion, value: string) => void;
  isPreview?: boolean;
  selectedValue?: string | number;
  onPreviewAnswerChange?: (questionId: string, value: string | number) => void;
  uiStrings?: {
    ratingLowLabel: string;
    ratingHighLabel: string;
    csatLowLabel: string;
    csatHighLabel: string;
  };
}

function getButtonColor(n: number, scale: RatingScale): string {
  if (scale === 5) {
    return [
      "var(--color-alias-error)",
      "var(--color-alias-warning)",
      "var(--color-alias-warning-light)",
      "var(--color-alias-success-light)",
      "var(--color-alias-success-dark)",
    ][n - 1];
  }
  // scale === 10 — mirror NPS-style banding for consistency
  if (n <= 3) return "var(--color-alias-error-light)";
  if (n <= 6) return "var(--color-alias-warning-light)";
  if (n <= 8) return "var(--color-alias-success-light)";
  return "var(--color-alias-success-dark)";
}

const motionTransition = { type: "spring" as const, stiffness: 400, damping: 17, duration: 0.2 };

export default function RatingQuestion({
  question,
  brandColor = "var(--color-alias-info)",
  isSelected,
  onAnswerChange,
  isPreview = false,
  selectedValue,
  onPreviewAnswerChange,
  uiStrings,
}: RatingQuestionProps) {
  const isCsat = String(question.questionType).toLowerCase() === "csat";
  const {
    scale,
    min,
    lowLabel,
    highLabel,
    backgroundColor,
    textColor: textColorOpt,
    borderColor,
    display,
  } = getRatingConfig(question, {
    lowLabel: isCsat ? uiStrings?.csatLowLabel : uiStrings?.ratingLowLabel,
    highLabel: isCsat ? uiStrings?.csatHighLabel : uiStrings?.ratingHighLabel,
  });
  const values = Array.from({ length: scale - min + 1 }, (_, i) => i + min);
  const labelsBelow = scale === 10;
  const textColor = textColorOpt ?? "var(--color-alias-foreground-on-color)";
  // Selection ring stays at 2px in textColor; the optional 1px borderColor
  // only applies in the unselected state.
  const baseBorder = borderColor ? `1px solid ${borderColor}` : "2px solid transparent";
  const selectedBorder = `2px solid ${textColor}`;

  const handleAnswerChange = (value: string | number) => {
    if (isPreview && onPreviewAnswerChange) {
      onPreviewAnswerChange(question.id, value);
    } else if (onAnswerChange) {
      onAnswerChange(question, value.toString());
    }
  };

  const isItemSelected = (value: number) => {
    if (isPreview) {
      return selectedValue === value;
    }
    return isSelected ? isSelected(question, value) : false;
  };

  const itemStyle = (n: number) => ({
    color: textColor,
    border: isItemSelected(n) ? selectedBorder : baseBorder,
    backgroundColor: backgroundColor ?? getButtonColor(n, scale),
    boxShadow: isItemSelected(n) ? "var(--box-shadow-md)" : "none",
  });
  const motionProps = (n: number) => ({
    animate: { filter: isItemSelected(n) ? "brightness(1.2)" : "brightness(1)" },
    whileHover: { scale: 1.1, boxShadow: "var(--box-shadow-md)" },
    whileTap: { scale: 0.95 },
    transition: motionTransition,
  });

  const renderPreviewItem = (n: number) => (
    <motion.button
      key={n}
      onClick={() => handleAnswerChange(n)}
      className="w-9 h-9 rounded-md font-semibold"
      style={itemStyle(n)}
      {...motionProps(n)}
    >
      {n}
    </motion.button>
  );

  const renderInteractiveItem = (n: number) => (
    <div key={n}>
      <input
        type="radio"
        id={`rating-${question.id}-${n}`}
        value={n}
        name={`rating-${question.id}`}
        className="sr-only"
        checked={isItemSelected(n)}
        onChange={() => handleAnswerChange(n)}
      />
      <motion.label
        htmlFor={`rating-${question.id}-${n}`}
        className="w-9 h-9 rounded-md font-semibold flex items-center justify-center cursor-pointer"
        style={itemStyle(n)}
        {...motionProps(n)}
      >
        <span>{n}</span>
      </motion.label>
    </div>
  );

  // For 1-5 the labels sit inline next to the buttons (compact, naturally
  // aligned with the 1 and 5). For 1-10 they go below the row; an
  // `inline-flex flex-col` wrapper sizes itself to the buttons row so the
  // labels' `w-full justify-between` aligns under the first and last button
  // instead of stretching to the surrounding question container.
  const renderRow = (renderItem: (n: number) => ReactNode) => {
    if (labelsBelow) {
      return (
        <div className="flex justify-center">
          <div className="inline-flex flex-col max-w-full">
            <div className="flex flex-wrap items-center justify-center gap-2">
              {values.map(renderItem)}
            </div>
            <div className="mt-1 flex justify-between w-full text-[11px] uppercase" style={{ color: "var(--sl-text-muted, var(--color-alias-neutral-dark))" }}>
              <span>{lowLabel}</span>
              <span>{highLabel}</span>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-wrap items-center justify-center gap-2">
        <span className="hidden md:inline text-[11px] uppercase mr-1" style={{ color: "var(--sl-text-muted, var(--color-alias-neutral-dark))" }}>
          {lowLabel}
        </span>
        {values.map(renderItem)}
        <span className="hidden md:inline text-[11px] uppercase ml-1" style={{ color: "var(--sl-text-muted, var(--color-alias-neutral-dark))" }}>
          {highLabel}
        </span>
      </div>
    );
  };

  // Full-width button display matching the single-select button style:
  // honors explicit custom colors when set, otherwise falls back to the
  // survey theme/brand styling. Low/high labels sit beneath the row.
  const renderButtonsDisplay = () => {
    const columnCount = Math.max(values.length, 1);
    const hasCustom = Boolean(backgroundColor || textColorOpt || borderColor);
    return (
      <div>
        <div
          role="radiogroup"
          aria-label={question.text || "Rating options"}
          className={`${choiceButtonStyles.buttonGrid} ${choiceButtonStyles.ratingButtonGrid}`}
          style={{
            gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
          }}
        >
          {values.map((n) => {
            const selected = isItemSelected(n);
            const colorStyle = hasCustom
              ? {
                  backgroundColor: backgroundColor ?? "var(--color-alias-white)",
                  border: `1px solid ${
                    borderColor ?? "var(--color-alias-neutral-light)"
                  }`,
                  boxShadow: selected
                    ? `inset 0 0 0 1px ${
                        borderColor ??
                        textColorOpt ??
                        "var(--color-alias-neutral-darkest)"
                      }`
                    : "none",
                  color:
                    textColorOpt ??
                    "var(--sl-text, var(--color-alias-neutral-darkest))",
                  fontWeight: selected ? 700 : 600,
                }
              : {
                  backgroundColor: selected ? brandColor : "transparent",
                  border: `1px solid ${
                    selected ? brandColor : "var(--color-alias-neutral-light)"
                  }`,
                  boxShadow: "none",
                  color: selected
                    ? "var(--color-alias-foreground-on-color)"
                    : "var(--sl-text, var(--color-alias-neutral-darkest))",
                  fontWeight: 600,
                };
            return (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => handleAnswerChange(n)}
                className={`${choiceButtonStyles.choiceButton} ${choiceButtonStyles.ratingButton}`}
                style={{
                  alignItems: "center",
                  borderRadius: "var(--border-radius-md, 6px)",
                  cursor: "pointer",
                  display: "flex",
                  fontFamily: "inherit",
                  justifyContent: "center",
                  lineHeight: 1.25,
                  margin: 0,
                  minHeight: "40px",
                  minWidth: 0,
                  textAlign: "center",
                  transition:
                    "background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
                  ...colorStyle,
                }}
              >
                {n}
              </button>
            );
          })}
        </div>
        <div
          className="mt-1 flex justify-between w-full text-[11px] uppercase"
          style={{ color: "var(--sl-text-muted, var(--color-alias-neutral-dark))" }}
        >
          <span>{lowLabel}</span>
          <span>{highLabel}</span>
        </div>
      </div>
    );
  };

  const buttonsDisplay = display === "buttons";
  return (
    <div className="mb-6">
      <div
        className={`text-sm font-medium ${
          buttonsDisplay ? "text-left" : "text-center"
        }`}
        style={{
          color: "var(--sl-text, var(--color-alias-neutral-darkest))",
          marginBottom: buttonsDisplay
            ? "calc(var(--spacing) * 4)"
            : "calc(var(--spacing) * 2)",
        }}
      >
        {question.text}
      </div>
      {buttonsDisplay
        ? renderButtonsDisplay()
        : renderRow(isPreview ? renderPreviewItem : renderInteractiveItem)}
    </div>
  );
}
